import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconButton } from "./IconButton";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** sm = confirm-style card; lg = full preferences-style workspace modal. */
  size?: "sm" | "lg";
  /** Render children edge-to-edge without the built-in title bar. */
  hideTitle?: boolean;
  children: React.ReactNode;
}

export function Dialog({ open, onClose, title, size = "sm", hideTitle = false, children }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => restoreRef.current?.focus?.();
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        className={cn(
          "flex flex-col rounded-lg bg-canvas border border-edge shadow-2xl outline-none",
          size === "lg" ? "w-full max-w-3xl h-[min(640px,85vh)]" : "w-full max-w-md max-h-[85vh]",
        )}
      >
        {!hideTitle && (
          <div className="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
            <h2 className="text-xl font-bold text-ink">{title}</h2>
            <IconButton label="Close" onClick={onClose}><X size={18} /></IconButton>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
